import { useSiteDashboard } from '@/hooks/useSiteDashboard';
import { useState, useEffect, useMemo } from 'react';
import {
  LayoutDashboard, DollarSign, Activity, Zap, Battery,
  AlertTriangle, Receipt, Banknote,
  TrendingUp, Wifi, LineChart, Building2, Gauge, HandCoins,
  Check, Plus, X, Minimize2, Maximize2, GripVertical,
  TimerOff,
} from 'lucide-react';
import type { OperatorDashboardData, OperatorDashboardPeriod } from '@evi/shared';
import { OperatorDashboardPeriodSchema } from '@evi/shared';
import { useTheme } from '@evi/skins';
import { useOperatorSummary } from '@/hooks/useOperatorSummary';
import { usePendingPayouts } from '@/hooks/usePendingPayouts';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { useAuthStore } from '@/store/auth';
import { useViewport } from '@/hooks/useViewport';
import { DashboardMapBackground, type DashboardMapInfoCardProps } from './DashboardMapBackground';
import { LocationInfoCard } from '@/components/LocationInfoCard';
import { KpiCard } from '@evi/ui';
import { useSalesBookSummary } from '@/hooks/useSalesBook';
import { SalesStaffConnect } from '../sales-team/SalesBookPages';
import type { SalesSummary } from '@/lib/salesTeam';
import { dashboardWidgetAllowed } from '@/lib/salesDashboard';
import styles from './DashboardPage.module.css';
import {
  DEFAULT_LAYOUT, dashboardDefaultLayout, dashboardLayoutKey, dashboardPlacements, dashboardView, isSuperAdminPersona,
  type DashboardView, type WidgetId,
} from './dashboardLayout';

import KpiRevenue from './widgets/KpiRevenue';
import KpiRevenueAllTime from './widgets/KpiRevenueAllTime';
import KpiSessions from './widgets/KpiSessions';
import KpiChargers from './widgets/KpiChargers';
import KpiEnergy from './widgets/KpiEnergy';
import ChartRevenue from './widgets/ChartRevenue';
import ChartSessions from './widgets/ChartSessions';
import ChartNetwork from './widgets/ChartNetwork';
import RecentSessionsWidget from './widgets/RecentSessionsWidget';
import AnomaliesWidget from './widgets/AnomaliesWidget';
import PendingPayoutsWidget from './widgets/PendingPayoutsWidget';
import TopRevenueLocationsWidget from './widgets/TopRevenueLocationsWidget';
import ChargerUptimeWidget from './widgets/ChargerUptimeWidget';
import SessionTrendsWidget from './widgets/SessionTrendsWidget';
import PerLocationRevenueWidget from './widgets/PerLocationRevenueWidget';
import ChargerUtilizationWidget from './widgets/ChargerUtilizationWidget';
import SettlementSummaryWidget from './widgets/SettlementSummaryWidget';
import StartTimeoutsWidget from './widgets/StartTimeoutsWidget';
import SessionsWidget from './widgets/SessionsWidget';

// ══════════════════════════════════════════════════════════════════════════
// Widget system
// ══════════════════════════════════════════════════════════════════════════

// WidgetId, the per-persona default layouts, their grid placements and the
// storage keys live in ./dashboardLayout.ts (pure, unit tested).

interface WidgetDef {
  id: WidgetId;
  title: string;
  description: string;
  icon: typeof LayoutDashboard;
  size: 'sm' | 'md' | 'lg';
}

const WIDGET_CATALOG: Record<WidgetId, WidgetDef> = {
  book_locations: { id: 'book_locations', title: 'Locations', description: 'Locations in your book', icon: Building2, size: 'sm' },
  book_partners: { id: 'book_partners', title: 'Partners', description: 'Partners in your book', icon: LayoutDashboard, size: 'sm' },
  book_sessions: { id: 'book_sessions', title: 'Sessions', description: 'All sessions in your book', icon: Activity, size: 'sm' },
  book_energy: { id: 'book_energy', title: 'Energy delivered', description: 'All kWh in your book', icon: Battery, size: 'sm' },
  kpi_revenue:     { id: 'kpi_revenue',     title: 'Revenue (30d)',    description: 'Total + 30-day revenue',             icon: DollarSign,   size: 'sm' },
  kpi_revenue_all_time: { id: 'kpi_revenue_all_time', title: 'All-time revenue', description: 'Every completed session, plus the last 30 days', icon: DollarSign, size: 'sm' },
  kpi_sessions:    { id: 'kpi_sessions',    title: 'Active Sessions',  description: 'Live + recent session count',        icon: Activity,     size: 'sm' },
  kpi_chargers:    { id: 'kpi_chargers',    title: 'Chargers Online',  description: 'Online / total chargers',            icon: Zap,          size: 'sm' },
  kpi_energy:      { id: 'kpi_energy',      title: 'Energy Delivered', description: 'Total kWh sold',                     icon: Battery,      size: 'sm' },
  chart_revenue:   { id: 'chart_revenue',   title: 'Revenue chart',    description: 'Revenue over time',                  icon: DollarSign,   size: 'md' },
  chart_sessions:  { id: 'chart_sessions',  title: 'Sessions donut',   description: 'Completed vs failed',                icon: Activity,     size: 'md' },
  chart_network:   { id: 'chart_network',   title: 'Network usage',    description: 'Charger utilization',                icon: Zap,          size: 'md' },
  recent_sessions: { id: 'recent_sessions', title: 'Recent sessions',  description: 'Latest 10 completed sessions',       icon: Receipt,      size: 'md' },
  sessions:        { id: 'sessions',        title: 'Sessions',         description: 'All sessions or active sessions, linked to Sessions', icon: Activity, size: 'md' },
  alerts:          { id: 'alerts',          title: 'Anomalies',        description: 'Faulted, offline, unstable chargers', icon: AlertTriangle, size: 'md' },
  pending_payouts: { id: 'pending_payouts', title: 'Pending payouts',  description: 'Next Stripe bank payout',            icon: Banknote,      size: 'sm' },
  top_revenue_locations: { id: 'top_revenue_locations', title: 'Top revenue locations', description: 'Highest-grossing locations (7d/30d)', icon: TrendingUp,    size: 'md' },
  charger_uptime:  { id: 'charger_uptime',  title: 'Charger uptime',   description: 'Online % + 24h sparkline',           icon: Wifi,          size: 'sm' },
  session_trends:  { id: 'session_trends',  title: 'Session trends',   description: 'Avg duration + kWh (30d)',           icon: LineChart,     size: 'md' },
  per_location_revenue: { id: 'per_location_revenue', title: 'Revenue by location', description: 'All locations in scope (7d/30d)', icon: Building2, size: 'md' },
  charger_utilization:  { id: 'charger_utilization',  title: 'Charger utilization', description: 'Active hours per charger (7d)',     icon: Gauge,     size: 'md' },
  settlement_summary:   { id: 'settlement_summary',   title: 'Owed to building',    description: 'Electricity reimbursement liability', icon: HandCoins, size: 'sm' },
  start_timeouts:       { id: 'start_timeouts',       title: 'Start timeouts',      description: 'Starts accepted but never plugged in (7d/30d/90d)', icon: TimerOff, size: 'md' },
};

interface StoredLayout {
  items: WidgetId[];
  minimized: WidgetId[];
}

function loadLayout(view: DashboardView): StoredLayout {
  const defaults = dashboardDefaultLayout(view);
  const salesBook = view === 'sales_book';
  const key = dashboardLayoutKey(view);
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { items: defaults, minimized: [] };
    const parsed = JSON.parse(raw) as StoredLayout;
    return {
      items: (parsed.items ?? defaults).filter((id) => id in WIDGET_CATALOG && dashboardWidgetAllowed(id, salesBook)),
      minimized: (parsed.minimized ?? []).filter((id) => id in WIDGET_CATALOG && dashboardWidgetAllowed(id, salesBook)),
    };
  } catch {
    return { items: defaults, minimized: [] };
  }
}

function saveLayout(layout: StoredLayout, key: string): void {
  try { localStorage.setItem(key, JSON.stringify(layout)); } catch { /* ignore */ }
}

function SalesBookMapCard(props: DashboardMapInfoCardProps) {
  return <LocationInfoCard {...props} operationalOnly />;
}

/**
 * Revenue chart period URL helpers — mirror the PWA HistoryPage
 * pattern at apps/web/src/pages/history/HistoryPage.tsx:185-219.
 * Read at mount, write back on chip change via
 * `history.replaceState` so the dashboard's `?period=7d` survives
 * page reloads + the browser-back stack stays clean.
 *
 * Defensive parse: an unrecognised `?period=foo` falls back to
 * the default rather than throwing — same posture the API takes
 * with the same value.
 */
function readPeriodFromUrl(fallback: OperatorDashboardPeriod): OperatorDashboardPeriod {
  try {
    const raw = new URLSearchParams(window.location.search).get('period');
    const parse = OperatorDashboardPeriodSchema.safeParse(raw);
    return parse.success ? parse.data : fallback;
  } catch {
    return fallback;
  }
}

function writePeriodToUrl(period: OperatorDashboardPeriod): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('period', period);
    window.history.replaceState({}, '', url);
  } catch {
    /* SSR / sandboxed contexts where history isn't available */
  }
}

interface DashboardPageProps {
  salesBook?: boolean;
  /** Programmatic navigation handler (App.tsx's pushState wrapper).
   *  Optional so the page still renders if a future host doesn't
   *  thread it through; widgets that need navigation gracefully
   *  degrade to a no-op when missing. */
  onNavigate?: (to: string) => void;
}

export function DashboardPage({ onNavigate, salesBook = false }: DashboardPageProps = {}) {
  useRecentRoute('Dashboard');
  const isSite = useSiteDashboard();
  // Map background renders in BOTH themes now. Earlier the light
  // theme opted out entirely; that killed the dashboard's visual
  // identity in light mode and was reverted on user feedback. The
  // map style swaps to mapbox/light-v11 in light mode and the
  // vignette gradients flip to a light-tinted overlay so neither
  // theme paints a foreign dark band across the page. Widget
  // glassmorphism (Commit 4) is already theme-invariant and reads
  // fine on either basemap.
  const { mode, theme } = useTheme();

  // Mobile + tablet drop the inline grid placements so widgets flow
  // into the responsive CSS grid (2 cols at <1024px, 2-cols-with-
  // size_sm-half-width at <640px). Without this gate the inline
  // `gridColumn: '1 / 7'` spans request implicit grid tracks that
  // blow the viewport past its actual width on mobile.
  // useViewport thresholds: mobile <640, tablet 640-1023, desktop >=1024.
  const viewport = useViewport();
  const isNarrow = viewport === 'mobile' || viewport === 'tablet';

  // Revenue chart period selector. State + URL search-param sync
  // live here at the dashboard root because the hook below keys
  // its query on it; ChartRevenue receives the value + setter and
  // renders the chip row above the chart. Pattern mirrors the
  // PWA HistoryPage (apps/web/src/pages/history/HistoryPage.tsx
  // lines 185-219) — read on mount via the URL, write back on
  // change via history.replaceState so chip taps don't pollute
  // the browser's back stack.
  // Admin landing default is 7d — operators want a week's rhythm on
  // first view, not a 24h slice. API default at apps/api stays 24h
  // (per 7c710ed) so any other consumer keeps backward compat. URL
  // ?period= still overrides — only the no-param fallback changes.
  const [period, setPeriodState] = useState<OperatorDashboardPeriod>(() => readPeriodFromUrl('7d'));
  const setPeriod = (next: OperatorDashboardPeriod): void => {
    setPeriodState(next);
    writePeriodToUrl(next);
  };

  // Single-shot operator summary replaces the old fan-out (stats fetch
  // + client-side session bucketing + hand-mocked station/driver names).
  // 30s staleTime + 60s poll lives inside useOperatorSummary.
  const summaryQ = useOperatorSummary(period);
  // Pre-fetch ineligibility flags at the page level so the grid map
  // can fully skip ineligible widget slots in view mode (no empty
  // bordered card). Edit mode still shows the slot so admins can
  // remove the widget from their layout if they want. Today only
  // pending_payouts has this self-hide path; future widgets that
  // don't apply to certain roles can join the same set.
  const pendingPayoutsQ = usePendingPayouts(!isSite && !salesBook);
  const bookQ = useSalesBookSummary(salesBook);
  const summary = summaryQ.data;
  const error = summaryQ.error instanceof Error
    ? summaryQ.error.message
    : summaryQ.isError ? 'Failed to load dashboard' : '';

  const [edit, setEdit] = useState(false);
  // The EVI team's own default (Warren, 28 Oct 2026) is the Super Admin
  // persona's alone; every other persona keeps its layout and its key.
  const superAdmin = useAuthStore((s) => isSuperAdminPersona(s.user));
  const view = dashboardView({ salesBook, isSite, superAdmin });
  const layoutKey = dashboardLayoutKey(view);
  // The layout carries the key it was loaded from. A persona change while
  // the page is mounted loads that persona's layout instead of saving this
  // one under the other persona's key (which would hide its new default).
  const [layoutState, setLayoutState] = useState<{ key: string; layout: StoredLayout }>(
    () => ({ key: layoutKey, layout: loadLayout(view) }),
  );
  if (layoutState.key !== layoutKey) setLayoutState({ key: layoutKey, layout: loadLayout(view) });
  const layout = layoutState.layout;
  const setLayout = (update: (p: StoredLayout) => StoredLayout) => {
    setLayoutState((s) => ({ ...s, layout: update(s.layout) }));
  };
  const [showCatalog, setShowCatalog] = useState(false);
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);

  useEffect(() => { saveLayout(layoutState.layout, layoutState.key); }, [layoutState]);

  const availableToAdd = useMemo(
    () => (Object.keys(WIDGET_CATALOG) as WidgetId[]).filter((id) => !layout.items.includes(id) && dashboardWidgetAllowed(id, salesBook) && (!isSite || DEFAULT_LAYOUT.includes(id))),
    [layout.items, isSite, salesBook]
  );

  const removeWidget = (id: WidgetId) => {
    setLayout((p) => ({ items: p.items.filter((x) => x !== id), minimized: p.minimized.filter((x) => x !== id) }));
  };
  // The catalog stays open after an add so the zero-widget recovery
  // flow can add several widgets in one visit; its own Done button
  // closes it (and the dormancy effect below then retires edit mode).
  const addWidget = (id: WidgetId) => {
    setLayout((p) => ({ ...p, items: [...p.items, id] }));
  };
  const toggleMinimize = (id: WidgetId) => {
    setLayout((p) => ({
      ...p,
      minimized: p.minimized.includes(id) ? p.minimized.filter((x) => x !== id) : [...p.minimized, id],
    }));
  };

  // No dashboard header (Warren's call: the map and the widgets ARE
  // the showcase). Edit mode stays in the code but DORMANT — its only
  // remaining entry is the zero-widget empty state below, which opens
  // the catalog in edit mode as the recovery path. This effect retires
  // edit the moment the catalog closes, so nobody can be stranded with
  // drag handles and no Done button. To resurface widget editing
  // deliberately later, render any control that calls setEdit(true).
  useEffect(() => {
    if (edit && !showCatalog) setEdit(false);
  }, [edit, showCatalog]);

  // Native drag-drop reorder
  const onDragStart = (idx: number) => setDraggingIdx(idx);
  const onDragOver = (e: React.DragEvent) => edit && e.preventDefault();
  const onDrop = (targetIdx: number) => {
    if (draggingIdx == null || draggingIdx === targetIdx) {
      setDraggingIdx(null);
      return;
    }
    setLayout((p) => {
      const next = [...p.items];
      const [moved] = next.splice(draggingIdx, 1);
      next.splice(targetIdx, 0, moved!);
      return { ...p, items: next };
    });
    setDraggingIdx(null);
  };

  // Theme modifier on the page wrapper drives the per-theme vignette
  // gradient colors via .pageLight / .pageDark scoped rules in the
  // module CSS. The `key` on DashboardMapBackground forces a full
  // remount when the theme family or mode flips — mapboxgl.Map's
  // `style` is constructor-time, so swapping basemaps requires tearing
  // the canvas down and re-instantiating with the new style URL.
  const themeClass = mode === 'light' ? styles.pageLight : styles.pageDark;

  return (
    <div className={`${styles.page} ${styles.pageWithMap} ${themeClass} ${salesBook ? styles.salesBook : ''} ${view === 'super_admin' ? styles.superAdmin : ''}`}>
      {/* Full-bleed map background — escapes the AppShell's 1400px
          content column via position:fixed so it spans the viewport
          edge-to-edge. Pinned beneath the sticky top nav (z-index 50)
          so the nav still wins. */}
      {/* No aria-hidden here. The map canvas itself IS decorative
          chrome and gets aria-hidden on its own host div inside
          MapContainer, but the AnimatePresence + LocationInfoCard
          subtree are siblings of that host — they need to stay
          focusable and discoverable by assistive tech. The old
          wrapper-level aria-hidden buried the card. */}
      <div className={styles.mapBackground}>
        <DashboardMapBackground
          key={`${theme.id}-${mode}`}
          mode={mode}
          siteView={isSite}
          fitToPoints={salesBook}
          onNavigate={onNavigate}
          InfoCardComponent={salesBook ? SalesBookMapCard : undefined}
        />
      </div>
      {/* Soft top + bottom gradients fade the map into the page
          chrome so widget edges don't fight a high-contrast basemap.
          Both are position:fixed siblings of the map — they scroll
          with the viewport, not the page content. Gradient colors
          flip via the .pageLight / .pageDark modifier above. */}
      <div className={styles.vignetteTop} aria-hidden />
      <div className={styles.vignetteBottom} aria-hidden />

      {salesBook && <div className={styles.salesBanner}><SalesStaffConnect /></div>}
      {/* Where the join link and the day's moves live (walkthrough B 009: a first sign-in found nothing here to do). */}
      {salesBook && <div className={styles.salesBanner}><a className={styles.todayLink} href="/sales-today" onClick={e => { if (onNavigate) { e.preventDefault(); onNavigate('/sales-today'); } }}>Your join link, new applications and today's moves are on Today →</a></div>}
      {salesBook && bookQ.error && <div className={styles.errorBanner}>{bookQ.error.message}</div>}
      {error && <div className={styles.errorBanner}>{error}</div>}
      {summaryQ.isLoading && !summary && <OperatorDashboardSkeleton />}

      {/* Widget catalog (drawer). Reached only from the zero-widget
          empty state now that the header is gone; carries its own Done
          so the recovery flow is self-contained. */}
      {edit && showCatalog && (
        <div className={styles.catalog}>
          <div className={styles.catalogHead}>
            <p className={styles.catalogTitle}>Add a widget</p>
            <button className={styles.catalogDone} onClick={() => setShowCatalog(false)}>
              <Check size={14} /> Done
            </button>
          </div>
          <div className={styles.catalogGrid}>
            {availableToAdd.map((id) => {
              const w = WIDGET_CATALOG[id];
              const Icon = w.icon;
              return (
                <button key={id} className={styles.catalogItem} onClick={() => addWidget(id)}>
                  <div className={styles.catalogIcon}><Icon size={16} /></div>
                  <div>
                    <p className={styles.catalogItemTitle}>{w.title}</p>
                    <p className={styles.catalogItemDesc}>{w.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Grid */}
      <div className={styles.grid}>
        {/* Map interaction zone (mobile + tablet only). Pinned to
            row 3 via CSS so it lands directly under the KPI 2x2
            block (rows 1-2) before any auto-flowed widget claims
            that row. Transparent + pointer-events: none so taps
            fall through to .mapBackground (position: fixed,
            z-index: 0, behind the .page stacking context) and
            reach mapbox-gl marker hit testing. Display: none on
            desktop keeps the existing layout from 5b082cb /
            b99e51b / bfdb6df untouched. Source order matters
            here: placing the spacer FIRST inside .grid means
            its explicit grid-row: 3 is honored before
            auto-flowed widgets compete for that row.
            On the site dashboard (Charger Owner and the other site
            personas) the camera frames the site in this zone
            (data-map-window, CameraController), the same as the
            Sales Partner map strip (Warren, 20 October 2026). The
            operator overview keeps its own camera. */}
        <div className={styles.mapZone} data-map-window={isSite ? '' : undefined} aria-hidden />
        {layout.items.map((id, idx) => {
          const def = WIDGET_CATALOG[id];
          if (!def || !dashboardWidgetAllowed(id, salesBook) || (isSite && !DEFAULT_LAYOUT.includes(id))) return null;
          // Self-hide widgets that don't apply to the current
          // caller (e.g. pending_payouts for third_party_admin).
          // In edit mode we still render the slot so the admin
          // can remove it from their layout — otherwise it would
          // be impossible to manage. In view mode the slot
          // disappears entirely; no empty card.
          if (
            id === 'pending_payouts'
            && pendingPayoutsQ.data
            && !pendingPayoutsQ.data.widget_applies
            && !edit
          ) {
            return null;
          }
          const minimized = layout.minimized.includes(id);
          // Default-shipped widgets carry an explicit grid placement
          // (DEFAULT_PLACEMENTS table above). Inline-style wins over
          // the .size_* class's grid-column: span, so placement-pinned
          // widgets render at their target cells while operator-added
          // widgets auto-flow with their .size_sm/md/lg class.
          //
          // At narrow viewports (mobile + tablet), DROP the inline
          // placement entirely so widgets flow into the responsive
          // grid via their .size_* class. The desktop placements
          // assume a 12-col grid and request gridColumn spans up to
          // '7 / 13'; at a 2-col grid those spans manufacture
          // implicit columns that overflow the viewport.
          const placement = dashboardPlacements(view)[id];
          return (
            <div
              key={id}
              data-dashboard-widget={id}
              className={`${styles.widget} ${styles[`size_${def.size}`]} ${salesBook && !minimized && (id.startsWith('book_') || id.startsWith('kpi_') || id === 'chart_network') ? styles.selfFramed : ''} ${minimized ? styles.minimized : ''} ${edit ? styles.editable : ''}`}
              style={isNarrow ? undefined : placement}
              draggable={edit}
              onDragStart={() => onDragStart(idx)}
              onDragOver={onDragOver}
              onDrop={() => onDrop(idx)}
            >
              {edit && (
                <div className={styles.widgetControls}>
                  <span className={styles.handle} title="Drag to reorder"><GripVertical size={14} /></span>
                  <button className={styles.ctrlBtn} onClick={() => toggleMinimize(id)} title={minimized ? 'Expand' : 'Minimize'}>
                    {minimized ? <Maximize2 size={12} /> : <Minimize2 size={12} />}
                  </button>
                  <button className={`${styles.ctrlBtn} ${styles.ctrlRemove}`} onClick={() => removeWidget(id)} title="Remove">
                    <X size={12} />
                  </button>
                </div>
              )}
              {!minimized && (
                <Widget
                  id={id}
                  summary={summary}
                  book={bookQ.data}
                  onNavigate={onNavigate}
                  period={period}
                  onPeriodChange={setPeriod}
                />
              )}
              {minimized && (
                <div className={styles.minimizedBody}>
                  <def.icon size={16} />
                  <span>{def.title}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {layout.items.length === 0 && (
        <div className={styles.emptyState}>
          <LayoutDashboard size={32} />
          <p>No widgets on your dashboard.</p>
          <button className={styles.addBtn} onClick={() => { setEdit(true); setShowCatalog(true); }}>
            <Plus size={14} /> Add your first widget
          </button>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Widget dispatcher
// ══════════════════════════════════════════════════════════════════════════

function Widget({
  id, summary, book, onNavigate, period, onPeriodChange,
}: {
  id: WidgetId;
  summary: OperatorDashboardData | undefined;
  book?: SalesSummary;
  onNavigate?: (to: string) => void;
  period: OperatorDashboardPeriod;
  onPeriodChange: (next: OperatorDashboardPeriod) => void;
}) {
  switch (id) {
    case 'book_locations': return <KpiCard label="Locations" value={book ? String(book.locations.length) : '...'} meta="In your book" />;
    case 'book_partners': return <KpiCard label="Partners" value={book ? String(book.partners) : '...'} meta="In your book" />;
    case 'book_sessions': return <KpiCard label="Sessions" value={book ? String(book.sessions ?? 0) : '...'} meta="All time in your book" />;
    case 'book_energy': return <KpiCard label="Energy delivered" value={book ? (book.energy_kwh ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 }) : '...'} unit="kWh" meta="All time in your book" />;
    case 'kpi_revenue':           return <KpiRevenue summary={summary} />;
    case 'kpi_revenue_all_time':  return <KpiRevenueAllTime />;
    case 'kpi_sessions':          return <KpiSessions summary={summary} />;
    case 'kpi_chargers':          return <KpiChargers summary={summary} />;
    case 'kpi_energy':            return <KpiEnergy summary={summary} />;
    case 'chart_revenue':         return <ChartRevenue summary={summary} period={period} onPeriodChange={onPeriodChange} />;
    case 'chart_sessions':        return <ChartSessions />;
    case 'chart_network':         return <ChartNetwork summary={summary} />;
    case 'recent_sessions':
      // Until D#5 this case rendered the operator-summary's
      // activeSessionsList — the catalog title was always "Recent
      // sessions" but the body showed currently-CHARGING sessions.
      // The new endpoint returns the latest 10 historical
      // completed/stopped/failed sessions, which is what the
      // catalog promised. Active-sessions data still lives on
      // summary.activeSessionsList for any future widget that
      // wants it.
      return <RecentSessionsWidget onNavigate={onNavigate} />;
    case 'sessions':              return <SessionsWidget onNavigate={onNavigate} activeTotal={summary?.totalActive} />;
    case 'alerts':                return <AnomaliesWidget onNavigate={onNavigate} />;
    case 'pending_payouts':       return <PendingPayoutsWidget onNavigate={onNavigate} />;
    case 'top_revenue_locations': return <TopRevenueLocationsWidget onNavigate={onNavigate} />;
    case 'charger_uptime':        return <ChargerUptimeWidget />;
    case 'session_trends':        return <SessionTrendsWidget />;
    case 'per_location_revenue':  return <PerLocationRevenueWidget onNavigate={onNavigate} />;
    case 'charger_utilization':   return <ChargerUtilizationWidget onNavigate={onNavigate} />;
    case 'settlement_summary':    return <SettlementSummaryWidget />;
    case 'start_timeouts':        return <StartTimeoutsWidget onNavigate={onNavigate} chargerCount={summary?.network.total} />;
    default:                      return null;
  }
}

/**
 * First-paint skeleton shown while the operator summary is loading.
 * Mirrors the shipped widget layout so the page doesn't jump when the
 * data resolves — four KPI cards, two chart cards, one table card.
 */
function OperatorDashboardSkeleton() {
  const tile = (h: number): React.CSSProperties => ({
    height: h,
    background: 'var(--evi-surface)',
    border: '1px solid var(--evi-border)',
    borderRadius: 'var(--evi-radius-lg, 14px)',
    boxShadow: 'var(--evi-card-glow-subtle, none)',
    animation: 'evi-skeleton-pulse 1.6s ease-in-out infinite',
  });
  return (
    <div aria-busy="true" aria-live="polite" style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'var(--admin-stats-columns, repeat(4, 1fr))', gap: 10 }}>
        {Array.from({ length: 4 }).map((_, i) => <div key={i} style={tile(92)} />)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'var(--admin-pair-columns, 1fr 1.4fr)', gap: 10 }}>
        <div style={tile(300)} />
        <div style={tile(300)} />
      </div>
      <div style={tile(220)} />
    </div>
  );
}
